import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import hash from "object-hash";
import { cloneDeep } from "lodash";
import TeacherSelections from "../TeacherSelections";
import magnify from "./icons/magnify.png";
import studentIcon from "./icons/student.svg";
import Button from "../../blocks/Button";
import EditModal from "./EditModal";
import "./index.scss";
import FaEdit from "react-icons/lib/fa/edit";
import { addBatchAssignments } from "../../../../services/assignment/addBatchAssignments";
import DomainIcon from "./DomainIcon";
import { ASSIGNMENT_TYPE } from "../../../../services/assignment/constants";
import manifestHelper from "../../../../services/assignment/manifest";
import SIS from "Services/StudentInformationService";
import { GetBrandedAppName } from "../../../../services/BrandingService";
import { useTranslation } from "react-i18next";
import { getAssessmentConfig } from "../../../../services/amiraLicenseService";
import { findSelectedAssessmentTagObjectFromSavedTags } from "../../../Teacher/Admin/containers/SchoolConfigurations/SelectAssessmentTags";
import { CONFIGURATION_DATA, DYSLEXIA_TASKS } from "../../../../constants/assessmentConfigPageConstants";

export default function Assignment(props) {
  const {districtId, schoolId} = props;
  const { type, students } = useSelector((state) => state.reports.assignment);

  const {t} = useTranslation("tracking_report")

  const [editModalData, setEditModalData] = useState(null);
  const [waiting, setWaiting] = useState(false);
  const [contentTags, setContentTags] = useState(null);
  // return to tracking if user refreshed (no redux data)
  useEffect(() => {
    if (!type) props.history.push("/teacher/reports/type/tracking");
  }, []);

  useEffect(() => {
    if(districtId && schoolId){
      getAssessmentConfig(districtId, schoolId).then((config) => {
        setContentTags(config?.contentTags);
      });
    }
  }, [districtId, schoolId]);

  return (
    <React.Fragment>
      <TeacherSelections
        containerStyle={{ maxWidth: 300 }}
        backText="BACK TO TRACKING REPORT"
        onBack={props.history.goBack}
        teacherSelectionTypes={{
          disabledRostering: true,
          pageTitle: t(getPageTitle()),
        }}
      />
      <div className="reports-container assignment">
        <EditModal
          editModalData={editModalData}
          type={type}
          onClose={() => setEditModalData(null)}
        />
        <div className="scroll-content">
          {renderType()}
          {renderStudents()}
          {renderTasks()}
        </div>
        {renderControls()}
      </div>
    </React.Fragment>
  );

  function getPageTitle() {
    if (type === ASSIGNMENT_TYPE.BENCHMARK)
      return "Assign Benchmark Assessment";
    if (type === ASSIGNMENT_TYPE.PROGRESS_MONITORING)
      return "Assign Progress Monitor";
    if (type === ASSIGNMENT_TYPE.CALIBRATION)
      return "Assign Calibration";
  }

  function renderType() {
    const id = type?.toLowerCase();
    return (
      <div>
        <div className="assessment-type">
          <img src={magnify} />
          <div>
            <div className="label">{t(`${id}_label`)}</div>
            <div className="description">{t(`${id}_description`)}</div>
          </div>
        </div>
      </div>
    );
  }

  function renderStudents() {
    return (
      <div className="section">
        <div className="section-info">
          <div className="section-label">{t("STUDENT ASSESSMENT CONFIGURATION")}:</div>
        </div>
        {studentGroups.map((group, i) => {
          const filtered = students.filter(group.filter);
          if (filtered.length === 0) return;
          return (
            <div className="student-group" key={i}>
              <div className="label-wrapper">
                <div className="label">{t(group.label)}</div>
              </div>
              {filtered.map((student) => (
                <Student
                  data={student}
                  key={student.studentId}
                  onClickEdit={() => setEditModalData(student)}
                  type={type}
                />
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  function renderTasks() {
    const types = [];
    if (type === ASSIGNMENT_TYPE.PROGRESS_MONITORING) {
      types.push(["assessment"]);
    } else {
      for (const s of students) {
        const { manifest, grade } = s;
        for (const m of manifest) { // the manifest comes from assignmentTemplate query
          const { activityType } = m;
          if (activityType === "dyslexia"){
            if(type !== ASSIGNMENT_TYPE.CALIBRATION){
              types.push(...getDyslexiaTasksByGrade(grade, contentTags));
            }else{
              types.push("calibration"); //###Use this for calibration for now. May need to develop new types
            }

          }else{
            types.push(activityType);
          }
        }
      }
    }

    return (
      <div className="section">
        <div className="section-info">
          <div className="section-label">{t("TASKS")}:</div>
          <div className="section-description">
            {t("tasks_description")}
          </div>
        </div>
        <table className="task-table">
          <thead>
            <tr>
              <th>{t("Task")}</th>
              <th style={{ width: "40%" }}>{t("Description")}</th>
              <th>{t("Reading Rope Domains")}</th>
            </tr>
          </thead>
          <tbody>
            {[...new Set(types)].map((type, i) => {
              const task = taskLookup[type];
              if (!task) {
                console.log("unsupported task type", type);
                return null;
              }
              const { domains } = task;
              return (
                <tr key={i}>
                  <td>
                    <div className="task-name">
                      <div className="task-number">{i + 1}</div>
                      <div>{t(`${type}_name`)}</div>
                    </div>
                  </td>
                  <td>{t(`${type}_info`)}</td>
                  <td>
                    {domains.map((domain) => (
                      <DomainIcon type={domain} key={domain} />
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  function renderControls() {
    return (
      <div className="controls">
        <Button
          style={{ width: 160 }}
          onClick={createAssignments}
          waiting={waiting}
        >
          {t("common:Create")}
        </Button>
        <Button type="secondary" onClick={props.history.goBack}>
          {t("common:Cancel")}
        </Button>
      </div>
    );
  }

  async function createAssignments() {
    const manifestLookup = {}; // mapping hash(manifest): manifest
    const studentBins = {}; // mapping hash(manifest): [studentId]

    for (const s of students) {
      const { manifest, studentId } = s;
      let thisManifest = manifest;
      //Override progress monitoring here if they didn't edit
      if (type === ASSIGNMENT_TYPE.PROGRESS_MONITORING && !s.edited) {
        thisManifest = [
          { activityType: "assessment", locale: "EN_US", sequence: "CONTINUE" },
        ];
      }

      const manifestId = hash(manifest);

      manifestLookup[manifestId] = thisManifest;
      if (!studentBins[manifestId]) studentBins[manifestId] = [];
      studentBins[manifestId].push(studentId);

      // set the student.metadata.esl to true if spanish was set
      const isEsl = manifest.map((m) => m.locale).includes("ES_MX");
      if (isEsl) {
        const metadata = cloneDeep(s.metadata);
        metadata.esl = true;
        SIS.updateStudentMetadata(s.studentId, metadata);
      }
    }

    // create batch assignments...
    // one request per manifestId
    setWaiting(true);
    for (const manifestId in studentBins) {
      const manifest = manifestLookup[manifestId];
      const payload = {
        entityIdList: studentBins[manifestId],
        assignmentType: type,
        manifest,
      };

      console.log("MAKING ASSIGNMENT", payload);
      const res = await addBatchAssignments(payload);
      console.log("res", res);
    }
    props.history.goBack();
  }
}

function Student(props) {
  const { data,type } = props;

  const {t} = useTranslation("tracking_report");

  //Make sure locales are unique since they're used as keys
  let localesInManifest = data.manifest.map((m) => m.locale);
  console.log("localesInManifest",localesInManifest)
  if(data.metadata && data.metadata.assessmentLanguageOverride){
    if(data.metadata.assessmentLanguageOverride === "ENGLISH_ONLY"){
      localesInManifest = ["EN_US"];
    }else {
        if(data.metadata.assessmentLanguageOverride === "SPANISH_ONLY"){
        localesInManifest = ["ES_MX"];
      }
    }
  }
  const locales = [...new Set(localesInManifest)];

  const sessions = manifestHelper.numBreaks(data.manifest) + 1;

  return (
    <div className="student">
      <div>
        <img src={studentIcon} />
        {data.first_name} {data.last_name}
      </div>
      <div>
        {t("sessions", { count: sessions })}
      </div>
      <div>
        {locales.map((locale) => (
          <div key={locale} className={`locale ${locale}`}>
            {localeLookup[locale]}
          </div>
        ))}
      </div>
      {type !== ASSIGNMENT_TYPE.CALIBRATION &&
        <div>
        <div className="edit" onClick={props.onClickEdit}>
          <FaEdit /> {t("Manage Language")}
        </div>
      </div>
      }
    </div>
  );
}

const localeLookup = {
  EN_US: "ENG",
  ES_MX: "ESP",
};

const studentGroups = [
  {
    label: "English Only",
    filter: (s) => {
      const locales = s.manifest.map((m) => m.locale);
      return locales.includes("EN_US") && !locales.includes("ES_MX");
    },
  },
  {
    label: "Spanish Only",
    filter: (s) => {
      const locales = s.manifest.map((m) => m.locale);
      return !locales.includes("EN_US") && locales.includes("ES_MX");
    },
  },
  {
    label: "English & Spanish",
    filter: (s) => {
      const locales = s.manifest.map((m) => m.locale);
      return locales.includes("EN_US") && locales.includes("ES_MX");
    },
  },
];

const taskLookup = {
  assessment: {
    domains: ["decoding", "phonemic", "recognition", "comprehension"],
  },
  nwf: {
    domains: ["dyslexia", "decoding"],
  },
  spelling: {
    domains: ["dyslexia", "decoding"],
  },
  listeningComprehension: {
    domains: ["dyslexia", "comprehension"],
  },
  vocabularyScreener: {
    domains: ["dyslexia", "comprehension"],
  },
  readingComprehension: {
    domains: ["comprehension"],
  },
  ran: {
    domains: ["dyslexia"],
  },
  letterSounds: {
    domains: ["dyslexia", "decoding"],
  },
  wordReading: {
    domains: ["dyslexia", "decoding"],
  },
  blending: {
    domains: ["dyslexia", "phonemic"],
  },
  calibration: {
    domains: ["dyslexia", "decoding"],
  },
  wordManipulation: {
    domains: ["dyslexia", "phonemic"],
  },
  nonsenseWordReplication: {
    domains: ["dyslexia", "phonemic"],
  },
  letterNamingAndSounds: {
    domains: ["dyslexia", "decoding"],
  },
};

export const getDyslexiaTasksByGrade = (grade, contentTags) => {
  const defaultTasks = getAmiraDefaultDyslexiaTasks(grade);
  const overriddenTaskValuesByTag = applySavedConfigurationTagOverrides(contentTags, defaultTasks, grade);
  return overriddenTaskValuesByTag;
};

/**
 * Get the default dyslexia tasks for a given grade
 * @param {Number} grade
 * @returns {Array} - an array of task objects that should be included in the dyslexia tasks
 */
export const getAmiraDefaultDyslexiaTasks = (grade) => {

  const tasks = CONFIGURATION_DATA.reduce((acc, config) => {
    if(DYSLEXIA_TASKS.filter(task => task.name === config.name).length){
      const gradeConfig = config.configuration[grade];
      if(gradeConfig && typeof gradeConfig === "object"){
        if(gradeConfig.value === true){
          acc.push(DYSLEXIA_TASKS.find(task => task.name === config.name));
        }
      }
    }
    return acc;
  }, []);

  return tasks;
};

/**
 * Use the saved assessment tag to determine if there are any task overrides for the dyslexia tasks
 * if there is, return the task values that should be included in the dyslexia tasks after applying the overrides
 * @param {Array} contentTags
 * @param {Array} defaultTasks 
 * @param {Number} gradeIndex
 * @returns {Array} - an array of task values that should be included in the dyslexia tasks
 */
export const applySavedConfigurationTagOverrides = (contentTags, defaultTasks, gradeIndex) => {
  const overrides = findSelectedAssessmentTagObjectFromSavedTags(contentTags)?.override;
  if(!overrides){
    return defaultTasks.map(task => task.value);
  }

  return defaultTasks.reduce((acc, task) => {
      if(!overrides?.[task.name]){ // any task not in the overrides should be included
        acc.push(task.value);
      }else if(overrides?.[task.name]?.[gradeIndex] === 1){ // if the task is in the overrides and the grade is set to 1 (required)
        acc.push(task.value);
      }
      return acc;
  }, []);
}